/**
 * Fee type categories
 */
export enum FeeTypeCategory {
  Percentage = 'percentage',
  PerMile = 'per_mile',
  PerHour = 'per_hour',
  PerVolume = 'per_volume',
  PerOrder = 'per_order',
  PerStop = 'per_stop',
  PerDrop = 'per_drop',
  PerLoad = 'per_load',
  PerNoLoad = 'per_no_load',
  Detention = 'detention',
  Demurrage = 'demurrage',
  OutOfRoute = 'out_of_route',
  PerRetain = 'per_retain',
  ShortLoad = 'short_load',
  Other = 'other',
}

/**
 * Gets the display unit for a fee type
 *
 * @param type - The fee type string
 * @returns The display unit string
 */
export const getFeeTypeUnit = (type: string): string => {
  switch (type) {
    case 'Base Freight %':
    case 'Base & Accessorial %':
    case 'Total Freight %':
    case 'Base & Surcharge %':
    case 'Surcharge %':
      return '%'
    case 'Per Mile':
      return 'per mile'
    case 'Per Hour':
      return 'per hour'
    case 'Per Volume':
      return 'per gallon'
    case 'Per Order':
      return 'per order'
    case 'Per Stop':
      return 'per stop'
    case 'Per Drop':
      return 'per drop'
    case 'Per Load':
      return 'per load'
    case 'Per No Load':
      return 'per no load'
    case 'Detention':
      return 'per hour'
    case 'Demurrage':
      return 'per day'
    case 'Out of Route':
      return 'per mile'
    case 'Per Retain':
      return 'per retain'
    case 'Short Load':
      return 'per load'
    default:
      return type
  }
}

/**
 * Gets the category of a fee type
 *
 * @param type - The fee type string
 * @returns The fee type category
 */
export const getFeeTypeCategory = (type: string): FeeTypeCategory => {
  switch (type) {
    case 'Base Freight %':
    case 'Base & Accessorial %':
    case 'Total Freight %':
    case 'Base & Surcharge %':
    case 'Surcharge %':
      return FeeTypeCategory.Percentage
    case 'Per Mile':
      return FeeTypeCategory.PerMile
    case 'Per Hour':
      return FeeTypeCategory.PerHour
    case 'Per Volume':
      return FeeTypeCategory.PerVolume
    case 'Per Order':
      return FeeTypeCategory.PerOrder
    case 'Per Stop':
      return FeeTypeCategory.PerStop
    case 'Per Drop':
      return FeeTypeCategory.PerDrop
    case 'Per Load':
      return FeeTypeCategory.PerLoad
    case 'Per No Load':
      return FeeTypeCategory.PerNoLoad
    case 'Detention':
      return FeeTypeCategory.Detention
    case 'Demurrage':
      return FeeTypeCategory.Demurrage
    case 'Out of Route':
      return FeeTypeCategory.OutOfRoute
    case 'Per Retain':
      return FeeTypeCategory.PerRetain
    case 'Short Load':
      return FeeTypeCategory.ShortLoad
    default:
      return FeeTypeCategory.Other
  }
}

/**
 * Checks if a fee type is percentage-based
 *
 * @param type - The fee type string
 * @returns True if the fee type is percentage-based
 */
export const isPercentageFeeType = (type: string | null | undefined): boolean => {
  if (!type) return false
  return (
    type.includes('%') ||
    getFeeTypeCategory(type) === FeeTypeCategory.Percentage ||
    type.toLowerCase().includes('percent')
  )
}
